import { Component, computed, signal } from '@angular/core';
import { DatePipe, NgForOf, NgIf } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DividerModule } from 'primeng/divider';
import { SelectButtonModule } from 'primeng/selectbutton';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputSwitchModule } from 'primeng/inputswitch';
import { MessagesModule } from 'primeng/messages';
import { MultiSelectModule } from 'primeng/multiselect';
import { CalendarModule } from 'primeng/calendar';
import { RadioButtonModule } from 'primeng/radiobutton';

import { EmailCertifiedNode } from '@modules/campaigns/Domain/Nodes/EmailCertifiedNode';
import { EmailCertifiedConfig } from '@modules/campaigns/Domain/Interfaces/INodeConfig';
import { WorkflowStateService } from '@shared/state/workflow-state.service';
import { NodeType } from '@modules/campaigns/Domain/Enums/NodeType';
import { CopiesService } from '@modules/copies/service/copies.service';
import { CopyModel } from '@models/copies/copies.model';
import { SendMessageInspectorBase } from '../send-message-inspector.base';

@Component({
  selector: 'app-email-certified-inspector',
  standalone: true,
  imports: [
    NgIf,
    NgForOf,
    FormsModule,
    TranslateModule,
    DividerModule,
    SelectButtonModule,
    InputNumberModule,
    InputSwitchModule,
    MessagesModule,
    MultiSelectModule,
    CalendarModule,
    RadioButtonModule,
    DatePipe,
  ],
  templateUrl: './email-certified-inspector.component.html',
  styleUrls: ['./email-certified-inspector.component.scss'],
})
export class EmailCertifiedInspectorComponent extends SendMessageInspectorBase<
  EmailCertifiedNode,
  EmailCertifiedConfig
> {
  protected override get channelType() {
    return 'certified_email';
  }
  protected override get i18nPrefix() {
    return 'DATA.MODULES.INSPECTORS.EMAIL_CERTIFIED';
  }

  readonly selectedTemplateIds = signal<string[]>([]);

  readonly previewSubject = computed(() => {
    const template = this.selectedTemplate();
    if (!template) return '';
    const block = template.blocks?.find((b) => b.type === 'subject' || b.alias === 'subject');
    return block?.translations?.[0]?.text ?? '';
  });

  override get copyIds(): string[] {
    return this.selectedTemplateIds();
  }

  protected override setCopyIds(ids: string[]) {
    this.selectedTemplateIds.set(ids);
  }

  protected override refreshCopyFromConfig(config: EmailCertifiedConfig) {
    this.selectedTemplateIds.set(config.copyIds ?? []);
  }

  override get nodeValue(): EmailCertifiedNode | null {
    const n = this.node();
    return n?.type === NodeType.SendCertifiedEmail ? (n as EmailCertifiedNode) : null;
  }

  protected override buildConfig(
    node: EmailCertifiedNode,
    copyIds: string[],
  ): EmailCertifiedConfig {
    return {
      ...(node.config as EmailCertifiedConfig),
      copyIds,
    } as EmailCertifiedConfig;
  }

  protected override extractPreviewMessage(template: CopyModel | null): string {
    if (!template) return '';
    const htmlBlock = template.blocks?.find(
      (b) =>
        b.type === 'emailHtml' ||
        b.type === 'html' ||
        b.alias === 'emailHtml' ||
        b.type === 'text' ||
        b.alias === 'text',
    );
    if (htmlBlock) return htmlBlock.translations?.[0]?.text ?? '';

    const textBlock = template.blocks?.find(
      (b) => b.type === 'emailText' || b.type === 'text' || b.alias === 'emailText',
    );
    return textBlock?.translations?.[0]?.text ?? '';
  }

  onTemplatesChange(ids: string[]) {
    this.selectedTemplateIds.set(ids);
    this.scheduleApplyChanges();
  }

  constructor(
    workflow: WorkflowStateService,
    translate: TranslateService,
    copiesService: CopiesService,
    route: ActivatedRoute,
  ) {
    super(workflow, translate, copiesService, route);
  }
}
